import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AccountListRow } from '../components/accounts/AccountListRow';
import { AccountFormModal } from '../components/accounts/AccountFormModal';
import {
  AccountBulkSalespersonModal,
  type BulkSalespersonMode,
} from '../components/accounts/AccountBulkSalespersonModal';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { useCrmData } from '../contexts/CrmDataContext';
import { usePortal } from '../contexts/PortalContext';
import { canAssignContactOnForeignAccount } from '../lib/accountVisibility';
import {
  getAccountsPageScroller,
  readAccountsListState,
  readAccountsScrollTop,
  writeAccountsListState,
  writeAccountsScrollTop,
} from '../lib/accountsListState';
import { canCreatePersonalCrmRecords } from '../lib/roleAccess';
import type { Client } from '../types/crm';
import { acraStatusForClient, useAccountAcraStatusMap } from '../hooks/useAccountAcraStatusMap';
import { withAcraRegisteredAddressFallback } from '../lib/acraRegistry';
import {
  buildAccountListEntries,
  filterAccountListEntries,
  sortAccountListEntries,
  type AccountListEntry,
  type AccountSortDirection,
  type AccountSortKey,
} from '../utils/accountHierarchy';

/** Header cells aligned with AccountListRow grid (Type | Account | ACRA | District | Salesperson). */
const LIST_HEADERS_CRM: Array<
  | { kind: 'sort'; key: AccountSortKey; label: string }
  | { kind: 'label'; label: string }
> = [
  { kind: 'sort', key: 'type', label: 'Type' },
  { kind: 'sort', key: 'account', label: 'Account' },
  { kind: 'label', label: 'ACRA' },
  { kind: 'sort', key: 'district', label: 'District' },
  { kind: 'sort', key: 'salesperson', label: 'Salesperson' },
];

/** IT Management — no salesperson column. */
const LIST_HEADERS_IT: Array<
  | { kind: 'sort'; key: AccountSortKey; label: string }
  | { kind: 'label'; label: string }
> = [
  { kind: 'sort', key: 'type', label: 'Type' },
  { kind: 'sort', key: 'account', label: 'Account' },
  { kind: 'label', label: 'ACRA' },
  { kind: 'sort', key: 'district', label: 'District' },
];

function initialAccountsListState() {
  return (
    readAccountsListState() ?? {
      search: '',
      sortKey: 'account' as AccountSortKey,
      sortDir: 'asc' as AccountSortDirection,
      expandedBrandParents: [] as string[],
      scrollTop: 0,
      lastOpenedCode: null as string | null,
    }
  );
}

export function AccountsPage() {
  const { kind: portalKind } = usePortal();
  const isItPortal = portalKind === 'it';
  const { getMyData, loading, refresh, effectiveRole, departmentSlug } = useCrmData();
  const initial = useMemo(() => initialAccountsListState(), []);
  const [search, setSearch] = useState(initial.search);
  const [sortKey, setSortKey] = useState<AccountSortKey>(initial.sortKey);
  const [sortDir, setSortDir] = useState<AccountSortDirection>(initial.sortDir);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [expandedBrandParents, setExpandedBrandParents] = useState<Set<string>>(
    () => new Set(initial.expandedBrandParents),
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<BulkSalespersonMode>('from_to');
  const scrollRestoredRef = useRef(false);
  const scrollRestoreDoneRef = useRef(false);
  const pendingScrollTopRef = useRef(initial.scrollTop);

  const canCreateAccount = canCreatePersonalCrmRecords(effectiveRole);
  const canBulkChangeSalesperson =
    !isItPortal && canAssignContactOnForeignAccount(effectiveRole, departmentSlug);
  const listHeaders = isItPortal ? LIST_HEADERS_IT : LIST_HEADERS_CRM;

  // IT has no salesperson column — drop that sort if restored from CRM session state.
  useEffect(() => {
    if (isItPortal && sortKey === 'salesperson') {
      setSortKey('account');
      setSortDir('asc');
    }
  }, [isItPortal, sortKey]);

  const allVisibleClients = useMemo(() => [...getMyData('clients')], [getMyData]);
  const { statusMap: acraStatusMap, registeredAddressMap } =
    useAccountAcraStatusMap(allVisibleClients);

  const clientsForList = useMemo(
    () =>
      allVisibleClients.map((client) =>
        withAcraRegisteredAddressFallback(client, registeredAddressMap),
      ),
    [allVisibleClients, registeredAddressMap],
  );

  const clients = useMemo(() => {
    const entries = buildAccountListEntries(clientsForList);
    const filtered = filterAccountListEntries(entries, search);
    return sortAccountListEntries(filtered, sortKey, sortDir);
  }, [clientsForList, sortKey, sortDir, search]);

  const listGroups = useMemo(() => {
    const groups: AccountListEntry[][] = [];
    let current: AccountListEntry[] = [];
    for (const entry of clients) {
      if (!entry.isSubCompany && current.length > 0) {
        groups.push(current);
        current = [];
      }
      current.push(entry);
    }
    if (current.length > 0) groups.push(current);
    return groups;
  }, [clients]);

  function persistListState(overrides?: { lastOpenedCode?: string | null; scrollTop?: number }) {
    const scrollTop =
      overrides?.scrollTop ??
      (scrollRestoredRef.current ? readAccountsScrollTop() : pendingScrollTopRef.current);
    writeAccountsListState({
      search,
      sortKey,
      sortDir,
      expandedBrandParents: [...expandedBrandParents],
      scrollTop,
      lastOpenedCode:
        overrides && 'lastOpenedCode' in overrides
          ? overrides.lastOpenedCode ?? null
          : readAccountsListState()?.lastOpenedCode ?? null,
    });
  }

  // Keep filters while browsing account details (component remounts on return).
  useEffect(() => {
    persistListState();
  }, [search, sortKey, sortDir, expandedBrandParents]);

  useEffect(() => {
    const scroller = getAccountsPageScroller();
    if (!scroller) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (!scrollRestoredRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        persistListState({ scrollTop: readAccountsScrollTop() });
      });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      scroller.removeEventListener('scroll', onScroll);
      persistListState({
        scrollTop: scrollRestoredRef.current
          ? readAccountsScrollTop()
          : pendingScrollTopRef.current,
      });
    };
  }, [search, sortKey, sortDir, expandedBrandParents]);

  // Restore scroll after list paint (retry until the dock `.main` scroller accepts the offset).
  useLayoutEffect(() => {
    if (scrollRestoreDoneRef.current) return;
    if (loading && clients.length === 0) return;

    const targetTop = pendingScrollTopRef.current;
    if (targetTop <= 0) {
      scrollRestoredRef.current = true;
      scrollRestoreDoneRef.current = true;
      return;
    }

    let attempts = 0;
    let raf = 0;

    const tryRestore = () => {
      writeAccountsScrollTop(targetTop);
      const current = readAccountsScrollTop();
      const stuck = Math.abs(current - targetTop) < 2;
      attempts += 1;

      if (stuck || attempts >= 24) {
        scrollRestoredRef.current = true;
        scrollRestoreDoneRef.current = true;
        persistListState({ scrollTop: current });
        return;
      }
      raf = requestAnimationFrame(tryRestore);
    };

    raf = requestAnimationFrame(tryRestore);
    return () => cancelAnimationFrame(raf);
  }, [loading, clients.length]);

  function handleOpenAccount(code: string) {
    persistListState({
      scrollTop: readAccountsScrollTop(),
      lastOpenedCode: code,
    });
  }

  const visibleIds = useMemo(() => clients.map((e) => String(e.client.id)), [clients]);
  const allVisibleSelected =
    canBulkChangeSalesperson &&
    visibleIds.length > 0 &&
    visibleIds.every((id) => selectedIds.has(id));

  function toggleSort(key: AccountSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  }

  function toggleBrands(parentId: string) {
    setExpandedBrandParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  }

  function toggleSelected(clientId: string, selected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(String(clientId));
      else next.delete(String(clientId));
      return next;
    });
  }

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  function openBulk(mode: BulkSalespersonMode) {
    setBulkMode(mode);
    setBulkOpen(true);
  }

  return (
    <div className="accounts-page">
      <PageHeader
        title="Accounts"
        description={
          isItPortal
            ? 'View and manage customer accounts for IT Management.'
            : 'View and manage customer accounts across your sales portfolio.'
        }
        actions={
          canCreateAccount ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              + New
            </button>
          ) : undefined
        }
      />

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, code, type…"
        action={
          <div className="accounts-page__toolbar-aside">
            <span className="page-count">
              {loading ? 'Loading…' : `${clients.length} account${clients.length === 1 ? '' : 's'}`}
            </span>
            <div className="page-actions-row__btns">
              {canBulkChangeSalesperson ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => openBulk('from_to')}
                  disabled={loading}
                >
                  Change salesperson (bulk)
                </button>
              ) : null}
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => void refresh()}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
        }
      />

      {canBulkChangeSalesperson && selectedIds.size > 0 ? (
        <div className="account-bulk-bar" role="region" aria-label="Selected accounts">
          <span className="account-bulk-bar__count">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            className="account-bulk-bar__btn"
            onClick={() => openBulk('selected')}
          >
            Change code
          </button>
          <button
            type="button"
            className="account-bulk-bar__btn account-bulk-bar__btn--ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear
          </button>
        </div>
      ) : null}

      {loading && clients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading accounts…</p>
        </div>
      ) : clients.length === 0 ? (
        <div className="empty-state">
          <p>No accounts match your filters.</p>
        </div>
      ) : (
        <div
          className={`account-list card${canBulkChangeSalesperson ? ' account-list--selectable' : ''}${
            isItPortal ? ' account-list--no-salesperson' : ''
          }`}
        >
          <div className="account-list__head" role="row">
            {canBulkChangeSalesperson ? (
              <label className="account-list__check-all">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  aria-label="Select all visible accounts"
                />
              </label>
            ) : null}
            {listHeaders.map((col) => {
              if (col.kind === 'label') {
                return (
                  <span key={col.label} className="account-list__col-label">
                    {col.label}
                  </span>
                );
              }
              const active = sortKey === col.key;
              const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <button
                  key={col.key}
                  type="button"
                  className={`account-list__sort-btn${active ? ' is-active' : ''}`}
                  onClick={() => toggleSort(col.key)}
                  aria-sort={ariaSort}
                >
                  <span>{col.label}</span>
                  {active ? (
                    <span className="account-list__sort-ind" aria-hidden>
                      {sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <span />
          </div>
          {listGroups.map((group) => {
            const [parent, ...subs] = group;
            const brandsExpanded = expandedBrandParents.has(parent.client.id);
            return (
              <div
                key={parent.client.id}
                className={`account-list-group${subs.length > 0 ? ' account-list-group--with-subs' : ''}`}
              >
                <AccountListRow
                  client={parent.client}
                  salespersonCode={parent.client.owner_id}
                  hideSalesperson={isItPortal}
                  acraStatus={acraStatusForClient(parent.client, acraStatusMap)}
                  depth={parent.depth}
                  isSubCompany={parent.isSubCompany}
                  parentName={parent.parentName}
                  hasSubCompanies={parent.hasSubCompanies}
                  isLastSub={parent.isLastSub}
                  selectable={canBulkChangeSalesperson}
                  selected={selectedIds.has(String(parent.client.id))}
                  onToggleSelected={toggleSelected}
                  onOpen={() => handleOpenAccount(parent.client.code)}
                />
                {subs.length > 0 ? (
                  <div
                    className={`account-list-group__children${
                      brandsExpanded ? '' : ' account-list-group__children--collapsed'
                    }`}
                  >
                    <button
                      type="button"
                      className="account-list-group__children-toggle"
                      aria-expanded={brandsExpanded}
                      onClick={() => toggleBrands(parent.client.id)}
                    >
                      <span className="account-list-group__children-label">
                        Brands under <strong>{parent.client.company_name}</strong>
                        <span className="account-list-group__children-count">{subs.length}</span>
                      </span>
                      <span className="account-list-group__children-chevron" aria-hidden>
                        {brandsExpanded ? '▾' : '▸'}
                      </span>
                    </button>
                    {brandsExpanded
                      ? subs.map((entry) => (
                          <AccountListRow
                            key={entry.client.id}
                            client={entry.client}
                            salespersonCode={entry.client.owner_id}
                            hideSalesperson={isItPortal}
                            acraStatus={acraStatusForClient(entry.client, acraStatusMap)}
                            depth={entry.depth}
                            isSubCompany={entry.isSubCompany}
                            parentName={entry.parentName}
                            parentClientType={parent.client.client_type}
                            hasSubCompanies={entry.hasSubCompanies}
                            isLastSub={entry.isLastSub}
                            selectable={canBulkChangeSalesperson}
                            selected={selectedIds.has(String(entry.client.id))}
                            onToggleSelected={toggleSelected}
                            onOpen={() => handleOpenAccount(entry.client.code)}
                          />
                        ))
                      : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <AccountFormModal
        open={modalOpen}
        client={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />

      {canBulkChangeSalesperson ? (
        <AccountBulkSalespersonModal
          open={bulkOpen}
          mode={bulkMode}
          clients={allVisibleClients}
          selectedIds={[...selectedIds]}
          onClose={() => setBulkOpen(false)}
          onDone={(updated) => {
            setSelectedIds(new Set());
            void refresh().then(() => {
              window.alert(
                updated === 0
                  ? 'No accounts needed updating.'
                  : `Updated salesperson code on ${updated} account${updated === 1 ? '' : 's'}.`,
              );
            });
          }}
        />
      ) : null}
    </div>
  );
}
